import { focusRing } from '@/components/ui/buttonStyles'
import { cn } from '@/utils/cn'
import { senseSources, type SenseProvenance } from '@/utils/provenance'

const LICENSE_URL = 'https://creativecommons.org/licenses/by-sa/4.0/'

/** Wiktionary titles use underscores for spaces; the rest is percent-encoded. */
export function wiktionaryUrl(lemma: string): string {
  return `https://en.wiktionary.org/wiki/${encodeURIComponent(lemma.replace(/ /g, '_'))}`
}

/**
 * The credit CC BY-SA 4.0 requires wherever Wiktionary text is shown: the
 * source with a link, the license with a link, and a note that it was changed
 * ("adapted" — we strip its template markup).
 *
 * Unlike SourceBadge this is not admin-configurable: attribution is a license
 * obligation, not a display preference. It links the lemma's page because
 * senses come from there, even for a form that has a page of its own.
 */
export function WiktionaryCredit({ lemma, senses, className }: {
  lemma: string
  senses: SenseProvenance[]
  className?: string
}) {
  if (!senseSources(senses).includes('wiktionary')) return null

  return (
    <p className={cn('text-xs text-muted font-light', className)}>
      Adapted from <CreditLink href={wiktionaryUrl(lemma)}>Wiktionary</CreditLink>
      <span aria-hidden="true" className="mx-1.5">·</span>
      <CreditLink href={LICENSE_URL}>CC BY-SA 4.0</CreditLink>
    </p>
  )
}

function CreditLink({ href, children }: { href: string; children: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        'rounded-sm underline decoration-line-soft underline-offset-2',
        'transition-colors duration-200 hover:text-accent hover:decoration-current',
        focusRing,
      )}
    >
      {children}
      <span className="sr-only"> (opens in a new tab)</span>
    </a>
  )
}
